import {
  KnowledgeBaseService,
  KnowledgeBaseResult,
} from '@/api/';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useStore } from '@nanostores/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  Loader2,
  Trash
} from 'lucide-react';
import { useEffect} from 'react';

/**
 * We'll update the selected workspace in the store and route
 * to the projects for this workspace.
 */
import { ReactNode } from 'react';

function SortableItem({ id, children }: { id: string; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

export type KnowledgeBaseListingProps = {
  organizationId: string;
};

export const KnowledgeBaseListing = ({ organizationId }: KnowledgeBaseListingProps) => {
  const org = useStore($org);
  const queryClient = useQueryClient();
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );
  // Query to get the workspaces of the organization
  const knowledgebases = useQuery<KnowledgeBaseResult[]>({
    queryKey: ['getKnowledgeBases', organizationId],
    enabled: !!organizationId,
    queryFn: async () => {
      const res = await KnowledgeBaseService.getKnowledgeBases(organizationId);
      return res;
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  

  function constructOrderMap(orders: string[]): Map<string, number> {
    const orderMap = new Map<string, number>();
    orders.forEach((item, index) => {
      orderMap.set(item, index);
    });
    return orderMap;
  }

  function sortKnowledgeBasesUsingSavedOrders(
    items: KnowledgeBaseResult[],
  ): KnowledgeBaseResult[] {
    const savedKnowledgeBaseOrders = JSON.parse(
      localStorage.getItem('knowledgebases_orders') || '{}',
    );

    if (Object.keys(savedKnowledgeBaseOrders)?.includes(organizationId)) {
      const knowledgeBaseOrder = savedKnowledgeBaseOrders[organizationId];

      // construct the order map
      const knowledgeBaseOrderMap = constructOrderMap(knowledgeBaseOrder);
      return items.sort((a, b) => {
        const orderA =
          knowledgeBaseOrderMap.get(a?.knowledgeBase?.id) ?? Number.MAX_SAFE_INTEGER;
        const orderB =
          knowledgeBaseOrderMap.get(b?.knowledgeBase?.id) ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
    }

    return items;
  }

  // Load workspace order from local storage
  useEffect(() => {
    if (knowledgebases?.isSuccess) {
      if (knowledgebases?.data?.length > 0) {
        $knowledgebases.set(knowledgebases?.data);
      }
    }
  }, [organizationId]);
  const selectedKnowledgeBase = useStore($knowledgebase);

  const handleDelete = async (knowledgeBaseId: string) => {
    try {
      // Call the backend delete API
      await KnowledgeBaseService.deleteKnowledgeBase(knowledgeBaseId);

      // Filter out the deleted item from the local state
      const updatedKnowledgeBases = knowledgebases.data?.filter(
        (kb) => kb.knowledgeBase.id !== knowledgeBaseId
      );

      // Update the query cache
      queryClient.setQueryData(['getKnowledgeBases', organizationId], updatedKnowledgeBases);

      // Update the local storage
      const savedKnowledgeBaseOrders = JSON.parse(
        localStorage.getItem('knowledgebases_orders') || '{}',
      );

      if (savedKnowledgeBaseOrders[organizationId]) {
        savedKnowledgeBaseOrders[organizationId] = savedKnowledgeBaseOrders[organizationId].filter(
          (id: string) => id !== knowledgeBaseId,
        );
        localStorage.setItem(
          'knowledgebases_orders',
          JSON.stringify(savedKnowledgeBaseOrders),
        );
      }
    } catch (error) {
      console.error("Failed to delete knowledge base:", error);
    }
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const updatedKnowledgeBases = (items: KnowledgeBaseResult[]) => {
        const oldIndex = items.findIndex(
          item => item.knowledgeBase.id === active.id,
        );
        const newIndex = items.findIndex(item => item.knowledgeBase.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      };

      // Update the workspace order in the local query cache
      queryClient.setQueryData(
        ['getKnowledgeBases', organizationId],
        updatedKnowledgeBases,
      );
      const newKnowledgeBases = updatedKnowledgeBases(knowledgebases.data || []);

      // update on the local storage
      const savedKnowledgeBaseOrders = JSON.parse(
        localStorage.getItem('knowledgebases_orders') || '{}',
      );

      if (Object.keys(savedKnowledgeBaseOrders)?.includes(organizationId)) {
        savedKnowledgeBaseOrders[organizationId] = newKnowledgeBases?.map(
          kb => kb.knowledgeBase.id,
        );
        localStorage.setItem(
          'knowledgebases_orders',
          JSON.stringify(savedKnowledgeBaseOrders),
        );
      }
    }
  };

  return (
    <>
      <ScrollArea className="h-[calc(100vh-200px)]">
        <div className="flex flex-col gap-2 pr-4">
          {knowledgebases.isLoading && (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}
          

          {knowledgebases.data?.length === 0 && (
            <div className="flex h-32 items-center justify-center">
              <p>No knowledgebases found.</p>
            </div>
          )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={knowledgebases.data?.map(kb => kb.knowledgeBase.id) || []}
              strategy={verticalListSortingStrategy}
            >
              {sortKnowledgeBasesUsingSavedOrders(knowledgebases?.data || [])?.map(
                kb => (
                  <SortableItem key={kb.knowledgeBase.id} id={kb.knowledgeBase.id}>
                    <Card
                      className={cn(
                        selectedKnowledgeBase?.knowledgeBase.id === kb.knowledgeBase.id &&
                          'ring-primary-muted ring-1 ring-inset',
                      )}
                      key={kb.knowledgeBase?.id}
                    >
                      <CardHeader className="flex flex-row gap-4 space-y-0">
                        <div className="flex flex-grow items-center gap-2">
                          <div className="space-y-1">
                            <CardTitle>
                              {kb.knowledgeBase.fileName}
                            </CardTitle>
                            <CardDescription>
                              <div className="flex items-center">
                                <Calendar className="mr-1 h-3 w-3" />
                                Created on{' '}
                                {new Date(
                                  kb.knowledgeBase.createdAtUtc,
                                ).toLocaleDateString()}
                              </div>
                              {kb.knowledgeBase.updatedAtUtc && ( // Only render if updatedAtUtc exists
                                <div className="flex items-center">
                                  <Calendar className="mr-1 h-3 w-3" />
                                  Updated on{' '}
                                  {new Date(kb.knowledgeBase.updatedAtUtc).toLocaleDateString()}
                                </div>
                              )}
                            </CardDescription>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600"
                          onClick={() => handleDelete(kb.knowledgeBase.id)}>
                          <Trash className="w-5 h-5" />
                        </Button>
                      </CardHeader>
                    </Card>
                  </SortableItem>
                ),
              )}
            </SortableContext>
          </DndContext>
        </div>
      </ScrollArea>
    </>
  );
};
